import { ia, iaRa } from 'lib/helpers/utility';

const LIKELY = /status|provider|payer|type|location|service|category/i;
const IDENTIFIER = /\bids?\b|number|code/i;
// When a row was entered or edited is an audit question, rarely where work starts.
const AUDIT_DATE = /creat|updat|modif/i;
// Free text and amounts need an operator chosen first ("contains", "is more than").
const OPERATOR_TYPES = ['search', 'text', 'range', 'number-range', 'number'];

const canBeChip = (filter) =>
  filter?.type === 'date-range' ||
  (!!(filter?.queryKey || filter?.queryFn) && !OPERATOR_TYPES.includes(filter?.type));

const rank = ([key, filter]) => {
  if (filter.quick === false || filter.default) return null;
  if (!canBeChip(filter)) return null;
  if (filter.quick) return Number(filter.quick) || 1;
  const name = `${key} ${filter.title || ''}`;
  if (IDENTIFIER.test(name) || AUDIT_DATE.test(name)) return null;
  if (filter.type === 'date-range') return 100;
  if (LIKELY.test(name)) return 200;
  return null;
};

/**
 * The filters to offer as chips that are not applied yet, as [key, filter]. A table that names
 * its quick filters (`quickKeys`, in order) gets exactly those, never padded out; otherwise the
 * first `limit` worth offering. `exclude` keys are never offered (a status the view tabs own).
 */
export const pickSuggestedFilters = (defaultFilters, filters, limit, options = {}) => {
  const { quickKeys, exclude = [] } = options;
  const offered = (key) => !filters?.[key] && !exclude.includes(key);

  if (Array.isArray(quickKeys)) {
    return quickKeys
      .filter((key) => canBeChip(defaultFilters?.[key]) && offered(key))
      .map((key) => [key, defaultFilters[key]]);
  }

  return Object.entries(defaultFilters || {})
    .filter(([key]) => offered(key))
    .map((entry, index) => ({ entry, index, rank: rank(entry) }))
    .filter((candidate) => candidate.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map(({ entry }) => entry);
};

export const emptyFilter = (filter) => ({
  ...filter,
  formula: filter.formula || 'is',
  values: filter.type === 'date-range' ? { startDate: null, endDate: null } : []
});

const hasFilterValue = (values) => {
  if (Array.isArray(values)) return values.length > 0;
  if (values && typeof values === 'object') {
    return Object.values(values).some(
      (value) => value !== null && value !== undefined && value !== ''
    );
  }
  return values !== null && values !== undefined && values !== '';
};

// Only a picked value puts the chip's filter in the table's state; closing it empty leaves no trace.
export const suggestionChange = (changeFilters, key, empty) => (update) =>
  changeFilters((prev) => {
    const base = prev?.[key] ? prev : { ...prev, [key]: [empty] };
    const next = typeof update === 'function' ? update(base) : update;
    const picked = iaRa(next?.[key]).filter((filter) => hasFilterValue(filter?.values));
    if (ia(picked)) return { ...next, [key]: picked };
    const { [key]: _removed, ...rest } = next || {};
    return rest;
  });
